import type { ArticleCategory } from '@erp/data-access/interfaces';

function matchesValue(value: string | null | undefined, id: string, name: string): boolean {
  if (!value) return false;
  const normalizedValue = value.trim().toLocaleLowerCase();
  return value === id || normalizedValue === name.trim().toLocaleLowerCase();
}

export function resolveArticleCategorySelection(
  article: { categoryId: string | null; subFamily: string | null },
  categories: ArticleCategory[],
): { categoryId: string; subFamily: string } {
  const category = article.categoryId
    ? categories.find((c) => matchesValue(article.categoryId, c.id, c.name))
    : undefined;
  const subCategory = article.subFamily
    ? categories.find((c) => matchesValue(article.subFamily, c.id, c.name))
    : undefined;

  if (subCategory?.parentId) {
    return { categoryId: subCategory.parentId, subFamily: subCategory.id };
  }

  if (category?.parentId) {
    return { categoryId: category.parentId, subFamily: category.id };
  }

  return {
    categoryId: category?.id ?? article.categoryId ?? '',
    subFamily: subCategory?.id ?? article.subFamily ?? '',
  };
}

export function resolveOptionSelection<T extends { id: string; name: string }>(
  value: string | null | undefined,
  options: T[],
): string {
  if (!value) return '';
  return options.find((option) => matchesValue(value, option.id, option.name))?.id ?? value;
}

export function getSubCategoryOptions(
  categories: ArticleCategory[],
  categoryId: string,
  selectedSubCategoryId: string,
): { value: string; label: string }[] {
  const options = categories
    .filter((c) => Boolean(categoryId) && c.parentId === categoryId)
    .map((c) => ({ value: c.id, label: c.name }));

  if (selectedSubCategoryId && !options.some((o) => o.value === selectedSubCategoryId)) {
    const selected = categories.find((c) => matchesValue(selectedSubCategoryId, c.id, c.name));
    options.push({
      value: selected?.id ?? selectedSubCategoryId,
      label: selected?.name ?? selectedSubCategoryId,
    });
  }

  return options;
}

export function getRootCategoryOptions(
  categories: ArticleCategory[],
  selectedCategoryId: string,
): { value: string; label: string }[] {
  const options = categories
    .filter((c) => !c.parentId)
    .map((c) => ({ value: c.id, label: c.name }));

  if (selectedCategoryId && !options.some((o) => o.value === selectedCategoryId)) {
    const selected = categories.find((c) => matchesValue(selectedCategoryId, c.id, c.name));
    options.push({
      value: selected?.id ?? selectedCategoryId,
      label: selected?.name ?? selectedCategoryId,
    });
  }

  return options;
}

export function getOptionsWithCurrent<T extends { id: string; name: string }>(
  options: T[],
  selectedValue: string,
): { value: string; label: string }[] {
  const mapped = options.map((option) => ({ value: option.id, label: option.name }));
  if (selectedValue && !mapped.some((option) => option.value === selectedValue)) {
    const selected = options.find((option) => matchesValue(selectedValue, option.id, option.name));
    mapped.push({
      value: selected?.id ?? selectedValue,
      label: selected?.name ?? selectedValue,
    });
  }
  return mapped;
}
